import React from 'react';
import { palette } from '@/app/theme';
import { Box, Card, FormLabel, Link, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useObjectContext } from '@/hooks/useObjectContext';
import { CollapsibleTaskGroup } from '@/components/CollapsibleTaskGroup';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { useUpdateRelationshipCompanyMutation } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import { showToast } from '@/features/toast/toastSlice';

const AboutEntry = ({ header, value, isEmail = false, isPhone = false }) => {
	const displayValue = value || '-';
	const href = !value
		? null
		: isEmail
			? `mailto:${value}`
			: isPhone
				? `tel:${String(value).replace(/\s/g, '')}`
				: null;

	return (
		<Stack>
			<FormLabel>{header}</FormLabel>
			<Typography variant='subtitle2' sx={{ color: 'grey' }}>
				{href ? (
					<Link
						href={href}
						underline='hover'
						sx={{
							color: 'inherit',
							'&:hover': { color: palette.folioBlue },
						}}
					>
						{displayValue}
					</Link>
				) : (
					displayValue
				)}
			</Typography>
		</Stack>
	);
};

export const RelationshipCompanyAboutCard = () => {
	const dispatch = useDispatch();
	const { relationshipCompany } = useObjectContext();
	const [updateRelationshipCompany] = useUpdateRelationshipCompanyMutation();
	const address = [
		relationshipCompany?.address,
		relationshipCompany?.city,
		relationshipCompany?.state,
		relationshipCompany?.postcode,
		relationshipCompany?.country,
	]
		.filter(Boolean)
		.join(', ');

	return (
		<Card
			data-testid='about-card'
			sx={{ p: 3, borderTopRightRadius: 0, borderTopLeftRadius: 0 }}
		>
			<Stack spacing={3}>
				<Typography variant='subtitle1'>About</Typography>
				<AboutEntry header='Company name' value={relationshipCompany?.name} />
				<AboutEntry header='Trading name' value={relationshipCompany?.tradingName} />
				<Box>
					<FormLabel required>Company owner</FormLabel>
					<OwnerSelect
						minimal
						showGroup
						initialId={relationshipCompany?.ownerDocId}
						onChange={async (user) => {
							const ownerDocId = user?.id;
							if (!relationshipCompany?.id || !ownerDocId) return;
							if (ownerDocId === relationshipCompany.ownerDocId) return;
							try {
								await updateRelationshipCompany({
									relationshipCompanyId: relationshipCompany.id,
									properties: { ownerDocId },
								}).unwrap();
							} catch (error) {
								dispatch(
									showToast({
										message:
											error?.data?.message ??
											error?.message ??
											'Unable to update company owner.',
										error: true,
									}),
								);
							}
						}}
					/>
				</Box>
				<AboutEntry header='Company phone' value={relationshipCompany?.phone} isPhone />
				<AboutEntry header='Company email' value={relationshipCompany?.email} isEmail />
				<AboutEntry header='Address' value={address} />
				<AboutEntry header='Website' value={relationshipCompany?.website} />
				<AboutEntry header='ABN / registration number' value={relationshipCompany?.abn} />
				<AboutEntry
					header='Industry / market segment'
					value={relationshipCompany?.industry}
				/>
				<AboutEntry header='Primary contact' value={relationshipCompany?.contactName} />
				<AboutEntry
					header='Primary contact email'
					value={relationshipCompany?.contactEmail}
					isEmail
				/>
				<AboutEntry
					header='Primary contact phone'
					value={relationshipCompany?.contactPhone}
					isPhone
				/>
				<CollapsibleTaskGroup />
			</Stack>
		</Card>
	);
};
